import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Organization } from '../types';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';
import { queryClient } from '../App';

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  isLoading: boolean;
  switchOrganization: (orgId: number) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

interface OrganizationProviderProps {
  children: React.ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadOrganizations = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setOrganizations([]);
      setCurrentOrganization(null);
      setIsLoading(false);
      return;
    }

    try {
      const orgs = await apiService.getUserOrganizations();
      setOrganizations(orgs);

      // Get stored organization ID or use first organization
      const storedOrgId = localStorage.getItem('current_org_id');
      let selectedOrg: Organization | null = null;

      if (storedOrgId) {
        selectedOrg = orgs.find((org: Organization) => org.id === parseInt(storedOrgId)) || null;
      }

      // If no stored org or stored org not found, use first organization
      if (!selectedOrg && orgs.length > 0) {
        const firstOrg = orgs[0];
        selectedOrg = firstOrg;
        localStorage.setItem('current_org_id', firstOrg.id.toString());
      }

      setCurrentOrganization(selectedOrg);
      
      // Set the organization header for all API calls
      if (selectedOrg) {
        apiService.setOrganizationContext(selectedOrg.id);
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
      setOrganizations([]);
      setCurrentOrganization(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  const switchOrganization = useCallback(async (orgId: number) => {
    const newOrg = organizations.find((org: Organization) => org.id === orgId);
    if (!newOrg) {
      console.error('Organization not found:', orgId);
      return;
    }

    setIsLoading(true);

    try {
      // Update local storage
      localStorage.setItem('current_org_id', orgId.toString());

      // Update API service context
      apiService.setOrganizationContext(orgId);

      // Update state
      setCurrentOrganization(newOrg);

      // Results may contain organization-scoped frozen submission evidence.
      // Reset (not just stale-mark) so the previous organization's data disappears.
      await queryClient.resetQueries({ queryKey: ['ummp-results'] });

      // Invalidate all organization-dependent queries using React Query
      // This is the single source of truth for data refetching
      await queryClient.invalidateQueries({ queryKey: ['sov-files'] });
      await queryClient.invalidateQueries({ queryKey: ['sov-file'] });
      await queryClient.invalidateQueries({ queryKey: ['inbox'] });
      await queryClient.invalidateQueries({ queryKey: ['submissions'] });
      await queryClient.invalidateQueries({ queryKey: ['property-images'] });

      // Dispatch event for components that need to clear local state (e.g., Verify.tsx)
      // Components should NOT use this for refetching - React Query handles that
      window.dispatchEvent(new CustomEvent('organization-changed', {
        detail: { organizationId: orgId, organization: newOrg }
      }));
    } finally {
      setIsLoading(false);
    }
  }, [organizations]);

  const refreshOrganizations = useCallback(async () => {
    setIsLoading(true);
    await loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);


  const value = useMemo<OrganizationContextType>(() => ({
    currentOrganization,
    organizations,
    isLoading,
    switchOrganization,
    refreshOrganizations,
  }), [currentOrganization, organizations, isLoading, switchOrganization, refreshOrganizations]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
